import type { BodyJson } from 'contracts/body';
import { chain } from 'obliterator';
import type {
  Color,
  Matrix4,
  MeshPhysicalMaterialParameters,
  Float32BufferAttribute,
} from 'three';
import {
  BufferGeometry,
  DoubleSide,
  BufferAttribute,
  Group,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshPhysicalMaterial,
} from 'three';

import { SimpleProgressController } from '@frontend/controllers';
import type { ProgressSignal } from '@frontend/controllers';
import {
  WaitUntilSetController,
  wait,
  addEventListenerAndCollectRemovers,
  runRemoveEventListeners,
} from '@frontend/utils';

// eslint-disable-next-line no-restricted-imports
import {
  getOrCreateThreeColor,
  getOrCreateThreeColorArray,
  type ColorRGBArray,
} from '../../colors';
// eslint-disable-next-line no-restricted-imports
import {
  COLOR_CODES,
  ENTITY_TYPES,
  MATERIAL_PROPERTIES,
  PART_TYPES,
} from '../../constants';
// eslint-disable-next-line no-restricted-imports
import { disposeThreeJS } from '../../geom-utils';
// eslint-disable-next-line no-restricted-imports
import type {
  AsyncLoadable,
  SyncLoadable,
} from '../../renderer/loadingPriorityQueue';
// eslint-disable-next-line no-restricted-imports
import type { HasRenderable } from '../../renderer/renderer';
import type Body from '../body';
import type Part from '../part';
import type {
  EdgeMetaData,
  FaceMetaData,
  GeometryData,
  MetaDataType,
  RefMetaData,
  STEPGeometry,
  TextData,
} from './STEPGeometry';
import { StepEdgeModel } from './StepEdgeModel';
import { StepFaceModel } from './StepFaceModel';
import { RefStyleAdapter, type StyleAdapter } from './StepStyleAdapter';
import { StepTextModel } from './StepTextModel';
import { StepVertexBody } from './StepVertexBody';
import { constructEntityName } from './utils';

const faceSplitCount = 200;
const edgeSplitCount = 300;
const verticeSplitCount = 600;
const textSplitCount = 400;

const EDGE_MATERIAL = new LineBasicMaterial({
  //Don't use color. Use vertexColors instead
  //color: getOrCreateThreeColor(COLOR_CODES.edge_color)
  linewidth: MATERIAL_PROPERTIES.LINEWIDTH,
  opacity: MATERIAL_PROPERTIES.OPACITY,
  transparent: true,
  vertexColors: true,
});

export const defaultEdgeColor = getOrCreateThreeColor(COLOR_CODES.edge_color);
export const defaultEdgeColorComponents = getOrCreateThreeColorArray(
  COLOR_CODES.edge_color,
);

export const defaultTextColor = getOrCreateThreeColor(COLOR_CODES.text_color);
//export const defaultTextColorComponents = getOrCreateThreeColorArray(
//  COLOR_CODES.text_color,
//);

export const defaultFaceColor = getOrCreateThreeColor(
  MATERIAL_PROPERTIES.COLOR,
);
export const defaultFaceColorComponents = getOrCreateThreeColorArray(
  MATERIAL_PROPERTIES.COLOR,
);

const FACE_MATERIAL_PROPS: MeshPhysicalMaterialParameters = {
  //Don't use color. Use vertexColors instead
  //color: getOrCreateThreeColor(MATERIAL_PROPERTIES.COLOR),
  emissive: MATERIAL_PROPERTIES.EMMISSIVE_COLOR,
  emissiveIntensity: 0.0,
  metalness: MATERIAL_PROPERTIES.METALNESS,
  opacity: 1.0, //opacity is set using color alpha channel
  reflectivity: MATERIAL_PROPERTIES.REFLECTIVITY,
  side: DoubleSide,
  transparent: true,
  vertexColors: true,
};

export class STEPBody implements AsyncLoadable, SyncLoadable, HasRenderable {
  public static createProgress(
    id: string,
    part: Part<PART_TYPES.STEP>,
  ): SimpleProgressController | undefined {
    let todoCount = 0;
    if (part.isLoaded) {
      const { faceCount, edgeCount, vertices, texts } = part.geometry;
      todoCount =
        faceCount +
        edgeCount +
        (vertices ? vertices.length : 0) +
        (texts ? texts.length : 0);
    }
    return new SimpleProgressController({
      id,
      description: `Loading Body Geometry for ${
        part.name || part.id
      } (instance ${id})`,
      todoCount,
      expectedMs: Math.ceil(0.1 * todoCount),
      start: false,
    });
  }

  public readonly type: PART_TYPES.STEP = PART_TYPES.STEP;
  public readonly id?: string;
  public readonly partUuid?: string; //actually partFileUuid
  public readonly name?: string;
  public readonly color: Color;
  public readonly colorRGB: ColorRGBArray;
  public readonly opacity: number;
  public readonly faces: Map<string, StepFaceModel> = new Map(); //lookup by name
  public readonly faceGeometries: BufferGeometry[] = []; //the faces are compacted into these BufferGeometry values
  public readonly facesGroup = new Group(); //a group of the faceRenderables for controlling visibility
  public readonly faceMaterial: MeshPhysicalMaterial;

  public readonly edges: Map<string, StepEdgeModel> = new Map(); //lookup by name
  public readonly edgeGeometries: BufferGeometry[] = []; //the edges are compacted into these BufferGeometry values
  public readonly edgesGroup = new Group(); //a group of the edgesRenderables for controlling visibility
  public readonly edgeMaterial: LineBasicMaterial = EDGE_MATERIAL.clone();
  public readonly vectorStyleByNameMap: Map<
    string,
    StyleAdapter | RefStyleAdapter
  > = new Map(); // map of edgeName + textName to Style, holds styling data of edges for paint-utils rendering

  public readonly vertices: Map<string, StepVertexBody> = new Map(); //lookup by name

  public readonly textsGroup = new Group();
  private waitUntilTextsLoaded?: WaitUntilSetController<void>; // = new WaitUntilSetController<void>();
  public readonly texts: Map<string, StepTextModel> = new Map(); //lookup by name

  private resumeController?: WaitUntilSetController<void>;
  private loaded?: Promise<number>; //presence indicates this.isLoading. Resolves when loaded
  private disposeFns: (() => void)[] = [];
  private isDisposed?: boolean;

  constructor(
    data: BodyJson,
    private readonly part: Part<PART_TYPES.STEP>,
    private readonly host: Body<PART_TYPES.STEP>,
    private _progress?: SimpleProgressController, //create progress in advance so it can be composed and composedProgress closed. Use STEPBody.createProgress()
    private readonly signal?: AbortSignal, //AbortSignal to abort loading
  ) {
    this.id = data.id;
    this.name = data.name;
    this.partUuid = data.partFileUuid || data.part_uuid; //pcad provides part_uuid

    this.color = getOrCreateThreeColor(data.color);
    this.colorRGB = getOrCreateThreeColorArray(this.color);
    this.opacity = data.opacity ? data.opacity : MATERIAL_PROPERTIES.OPACITY;

    if (this.part.isLoaded && this._progress) {
      const { faceCount, edgeCount, vertices, texts } = this.part.geometry;
      const todoCount =
        faceCount +
        edgeCount +
        (vertices ? vertices.length : 0) +
        (texts ? texts.length : 0);
      if (todoCount && this._progress.todoCount !== todoCount) {
        this._progress.todoCount = todoCount;
      }
    }
    this.edgesGroup.visible = false; //initially hidden until it is explicitly set visible & painted
    this.facesGroup.visible = false; //initially hidden until it is explicitly set visible & painted
    this.textsGroup.visible = false; //initially hidden until it is explicitly set visible & painted

    this.edgesGroup.name = `edges|${this.id}`;
    this.facesGroup.name = `faces|${this.id}`;
    this.textsGroup.name = `text|${this.id}`;

    this.faceMaterial = new MeshPhysicalMaterial({
      ...FACE_MATERIAL_PROPS,
      ...this.host.specialMaterialProps,
    });
    if (signal) {
      addEventListenerAndCollectRemovers(
        this.disposeFns,
        signal,
        'abort',
        () => {
          this.dispose();
        },
      );
    }
  }

  public static factory(
    data: BodyJson,
    part: Part<PART_TYPES.STEP>,
    host: Body<PART_TYPES.STEP>,
    signal?: AbortSignal, //AbortSignal to abort loading
  ): [STEPBody, ProgressSignal<SimpleProgressController>?] {
    const progress = STEPBody.createProgress(`${data.id}-STEPBody`, part);
    return [new STEPBody(data, part, host, progress, signal), progress?.signal];
  }
  public dispose() {
    if (this.isDisposed) return;
    this.isDisposed = true;
    runRemoveEventListeners(this.disposeFns);
    this.abortLoad();
    disposeThreeJS(
      this.facesGroup,
      this.edgesGroup,
      this.textsGroup,
      this.texts,
      this.faceGeometries,
      this.edgeGeometries,
    );
    for (const mapCollection of [
      this.faces,
      this.edges,
      this.vertices,
      this.texts,
    ]) {
      for (const o of mapCollection.values()) {
        o.dispose();
      }
      mapCollection.clear();
    }
    this.vectorStyleByNameMap.clear();
  }

  public get renderable(): Group {
    return this.host.group;
  }

  public get fullDetailRep(): Group {
    return this.host.fullDetailRep;
  }

  public get transform(): Matrix4 {
    return this.host.transform;
  }

  public get body() {
    return this.host;
  }

  public get isMaterial(): boolean | undefined {
    return this.host.isMaterial;
  }

  public get faceVerticesCount(): number {
    return this.part.geometry.faceVerticesCount;
  }

  public get edgeVerticesCount(): number {
    return this.part.geometry.edgeVerticesCount;
  }

  public get geometryVerticesCount(): number {
    return this.part.geometry.geometryVerticesCount;
  }

  public get edgeMetaData() {
    if (!this.part.isLoaded) {
      throw new Error(`Body's part's geometry has not been loaded`);
    }
    return this.part.geometry.edgeMetaData;
  }

  public get specialMaterialProps():
    | MeshPhysicalMaterialParameters
    | undefined {
    return this.host.specialMaterialProps;
  }

  public get partGeometry(): STEPGeometry {
    return this.part.geometry;
  }

  public getObjsByType(type: ENTITY_TYPES) {
    return type === ENTITY_TYPES.FACE
      ? this.faces
      : type === ENTITY_TYPES.EDGE
      ? this.edges
      : type === ENTITY_TYPES.VERTEX
      ? this.vertices
      : type === ENTITY_TYPES.TEXT
      ? this.texts
      : undefined;
  }

  public get progress(): ProgressSignal<SimpleProgressController> | undefined {
    return this._progress?.signal;
  }

  public get isLoaded(): boolean {
    if (!this.part.isLoaded) {
      return false;
    }
    if (this._progress) {
      return false;
    }
    const { faceCount, edgeCount, vertices, texts } = this.part.geometry;
    return (
      this.part.isLoaded &&
      this.faces.size === faceCount &&
      this.edges.size === edgeCount &&
      (vertices
        ? vertices.length === this.vertices.size
        : this.vertices.size === 0) &&
      (texts ? texts.length === this.texts.size : this.texts.size === 0)
    );
  }

  public get isLoading(): boolean {
    return !!this.loaded;
  }

  public pause() {
    if (this._progress && !this.resumeController && !this.signal?.aborted) {
      if (this.part.isLoading) {
        this.part.pause();
      }
      this.resumeController = new WaitUntilSetController(this.signal);
    }
  }

  public get isPaused(): boolean {
    return !!this.resumeController || this.part.isPaused;
  }

  public resume() {
    if (this.resumeController && !this.signal?.aborted) {
      if (this.part.isLoading) {
        this.part.resume();
      }
      this.resumeController.set();
      delete this.resumeController;
    }
  }

  public abortLoad() {
    if (this.loaded) {
      this.pause(); //force pause so we can reject
      this.loaded.catch(() => Promise.resolve()); //the promise's rejection should be handled at least once
      delete this.loaded;
    }
    this.part.abortLoad();
    if (this.resumeController) {
      this.resumeController.setReject();
      delete this.resumeController;
    }
    if (this._progress) {
      this._progress.done();
    }
  }

  public load(): void {
    if (this.isLoaded || this.signal?.aborted) {
      return;
    }
    if (this.isLoading) {
      throw new Error(
        'Cannot load body synchronously that already started async loading',
      );
    }
    if (!this.part.isLoaded) {
      //Should be loaded already, but if not, load it
      this.part.load();
    }
    //Re-calculate todoCount for progress because it may have changed if this.part.loadAsync() just ran
    const {
      faceCount,
      edgeCount,
      vertices,
      texts,
      faceGeometries,
      faceMetaData,
      edgeGeometries,
      edgeMetaData,
    } = this.part.geometry;
    const todoCount =
      faceCount +
      edgeCount +
      (vertices ? vertices.length : 0) +
      (texts ? texts.length : 0);
    this._progress?.start(todoCount);
    this.loadFaceGeometries(faceGeometries);

    for (const metaData of faceMetaData.values()) {
      this.loadFaceMetaData(metaData);
    }

    this.loadEdgeGeometries(edgeGeometries);

    for (const metaData of edgeMetaData.values()) {
      this.loadEdgeMetaData(metaData);
    }

    if (vertices) {
      for (const vertex of vertices) {
        const vertexBody = new StepVertexBody(vertex, this);
        this.vertices.set(vertexBody.name, vertexBody);
        //IMPORTANT: Don't add the vertex to this.host.fullDetailRep yet because it impacts bounding box calculation
        //because the vertex's sphere has size. But it eventually needs to be added to host's group
        //for intersection tests that occur when hovering mouse over vertex.
        //* It will be added by the finalize() method in Body when the boundBox is established
        //this.group.add(vertexBody.vertex);
      }
    }

    if (texts && texts.length) {
      if (!this.waitUntilTextsLoaded) {
        this.waitUntilTextsLoaded = new WaitUntilSetController();
      }
      //No await here because this is sync load().
      Promise.allSettled((texts ?? []).map((text) => this.loadText(text))).then(
        () => {
          if (this.waitUntilTextsLoaded) {
            this.waitUntilTextsLoaded.set();
          }
        },
      );
      //IMPORTANT: Don't add the textsGroup to this.host.fullDetailRep yet because it impacts bounding box calculation
      //because the text's sphere has size. But it eventually needs to be added to host's group
      //for intersection tests that occur when hovering mouse over text.
      //* It will be added by the finalize() method in Body when the boundBox is established
      //  this.group.add(this.obj.textsGroup)
    } else {
      //No texts to load, but there could be something waiting for them. So signal we're done
      if (this.waitUntilTextsLoaded) {
        this.waitUntilTextsLoaded.set();
      }
    }

    this.loadRefs();
    if (this._progress) {
      this._progress.done();
      delete this._progress;
    }
    delete this.loaded;
    this.resume(); //cleanup resumeController if it exists
  }

  public loadAsync(lastPause: number = performance.now()): Promise<number> {
    if (this.isLoaded || this.signal?.aborted) {
      return Promise.resolve(lastPause);
    } else if (!this.loaded) {
      this.loaded = (async (): Promise<number> => {
        if (!this.part.isLoaded) {
          //eslint-disable-next-line no-param-reassign
          lastPause = await this.part.loadAsync(lastPause);
        }
        try {
          //Re-calculate todoCount for progress because it may have changed if this.part.loadAsync() just ran

          const {
            faceCount,
            edgeCount,
            vertices,
            texts,
            faceGeometries,
            faceMetaData,
            edgeGeometries,
            edgeMetaData,
          } = this.part.geometry;
          const todoCount =
            faceCount +
            edgeCount +
            (vertices ? vertices.length : 0) +
            (texts ? texts.length : 0);
          this._progress?.start(todoCount);

          this.loadFaceGeometries(faceGeometries);

          for (const [index, metaData] of faceMetaData.entries()) {
            //Every faceSplitCount faces, consider pausing (if paused) or deferring after 50ms of continuous work so we don't block UI
            //Note: It would be nice to make this if block a macro or fn. I am not using fn for performance reasons
            //because the fn would have to be async and would always defer each loop. But when inlined here it is only defer'ing when needed
            //Can't use macro because they do not exist in js/ts yet.
            if (index % faceSplitCount === 0) {
              //if paused, wait till it resumes
              if (this.resumeController) {
                await this.resumeController.waitUntilSet;
                //eslint-disable-next-line no-param-reassign
                lastPause = performance.now();
              }
              //defer after 50ms of work so that UI is not blocked
              const now = performance.now();
              if (now - lastPause > 50) {
                await wait();
                //eslint-disable-next-line no-param-reassign
                lastPause = now;
              }
              if (this.signal?.aborted) {
                break;
              }
              this._progress?.addProgress(edgeSplitCount);
            }
            this.loadFaceMetaData(metaData);
          }

          this.loadEdgeGeometries(edgeGeometries);

          for (const [index, metaData] of edgeMetaData.entries()) {
            //Every edgeSplitCount faces, consider pausing (if paused) or deferring after 50ms of continuous work so we don't block UI
            //Note: It would be nice to make this if block a macro or fn. I am not using fn for performance reasons
            //because the fn would have to be async and would always defer each loop. But when inlined here it is only defer'ing when needed
            //Can't use macro because they do not exist in js/ts yet.
            if (index % edgeSplitCount === 0) {
              //if paused, wait till it resumes
              if (this.resumeController) {
                await this.resumeController.waitUntilSet;
                //eslint-disable-next-line no-param-reassign
                lastPause = performance.now();
              }
              //defer after 50ms of work so that UI is not blocked
              const now = performance.now();
              if (now - lastPause > 50) {
                await wait();
                //eslint-disable-next-line no-param-reassign
                lastPause = now;
              }
              if (this.signal?.aborted) {
                break;
              }
              this._progress?.addProgress(edgeSplitCount);
            }

            this.loadEdgeMetaData(metaData);
          }

          if (vertices) {
            for (const [index, vertex] of vertices.entries()) {
              //Every verticeSplitCount faces, consider pausing (if paused) or deferring after 50ms of continuous work so we don't block UI
              //Note: It would be nice to make this if block a macro or fn. I am not using fn for performance reasons
              //because the fn would have to be async and would always defer each loop. But when inlined here it is only defer'ing when needed
              //Can't use macro because they do not exist in js/ts yet.
              if (index % verticeSplitCount === 0) {
                //if paused, wait till it resumes
                if (this.resumeController) {
                  await this.resumeController.waitUntilSet;
                  //eslint-disable-next-line no-param-reassign
                  lastPause = performance.now();
                }
                //defer after 50ms of work so that UI is not blocked
                const now = performance.now();
                if (now - lastPause > 50) {
                  await wait();
                  //eslint-disable-next-line no-param-reassign
                  lastPause = now;
                }
                if (this.signal?.aborted) {
                  break;
                }
                this._progress?.addProgress(verticeSplitCount);
              }
              const vertexBody = new StepVertexBody(vertex, this);
              this.vertices.set(vertexBody.name, vertexBody);
              //IMPORTANT: Don't add the vertex to this.host.fullDetailRep yet because it impacts bounding box calculation
              //because the vertex's sphere has size. But it eventually needs to be added to host's group
              //for intersection tests that occur when hovering mouse over vertex.
              //* It will be added by the finalize() method in Body when the boundBox is established
              //this.group.add(vertexBody.vertex);
            }
          }

          const textsLoaded: Promise<unknown>[] = [];
          if (texts && texts.length) {
            for (const [index, text] of texts.entries()) {
              if (index % textSplitCount === 0) {
                //if paused, wait till it resumes
                if (this.resumeController) {
                  await this.resumeController.waitUntilSet;
                  //eslint-disable-next-line no-param-reassign
                  lastPause = performance.now();
                }
                //defer after 50ms of work so that UI is not blocked
                const now = performance.now();
                if (now - lastPause > 50) {
                  await wait();
                  //eslint-disable-next-line no-param-reassign
                  lastPause = now;
                }
                if (this.signal?.aborted) {
                  break;
                }
                this._progress?.addProgress(textSplitCount);
              }
              textsLoaded.push(this.loadText(text));
            }
            await Promise.allSettled(textsLoaded);
            //IMPORTANT: Don't add the textsGroup to this.host.fullDetailRep yet because it impacts bounding box calculation
            //because the text's sphere has size. But it eventually needs to be added to host's group
            //for intersection tests that occur when hovering mouse over text.
            //* It will be added by the finalize() method in Body when the boundBox is established
            //  this.group.add(this.obj.textsGroup)
          }
          this.loadRefs();
        } finally {
          if (this.waitUntilTextsLoaded) {
            this.waitUntilTextsLoaded.set();
          }
          if (this._progress) {
            this._progress.done();
            delete this._progress;
          }
          delete this.loaded;
          this.resume(); //cleanup resumeController if it exists
        }
        return lastPause;
      })();
    } else if (this.isPaused) {
      this.resume();
    }
    return this.loaded;
  }

  public updateTodoCount() {
    if (this.part.isLoaded && this._progress) {
      const { faceCount, edgeCount, vertices, texts } = this.part.geometry;
      const todoCount =
        faceCount +
        edgeCount +
        (vertices ? vertices.length : 0) +
        (texts ? texts.length : 0);
      this._progress.todoCount = todoCount;
    }
  }

  public scaleVertexes(zoomFactor: number) {
    for (const { vertex } of this.vertices.values()) {
      vertex.scale.set(zoomFactor, zoomFactor, zoomFactor);
    }
  }

  //updateColors() is intended to use after making a large number of setColor/setOpacity calls to faces and edges
  public updateColors() {
    for (const geometry of chain(this.faceGeometries, this.edgeGeometries)) {
      const colorAttribute = geometry.attributes
        .color as Float32BufferAttribute;
      colorAttribute.needsUpdate = true;
      colorAttribute.version++;
    }
  }

  private loadFaceGeometries(
    faceGeometries: Required<GeometryData>['faceGeometries'],
  ) {
    //Note: No pausing for faceGeometries creation because there are only a few (with many already packed into the this.part.geometry.faceGeometries)
    for (const faceGeometry of faceGeometries) {
      //Note: color attribute assigned after hydrating faceMetaData
      const facesBufferGeometry = new BufferGeometry()
        .setAttribute('position', faceGeometry.positions)
        .setAttribute(
          'color',
          //Note: First argument of BufferAttribute must be typed array. (Typescript doesn't specify this but src does)
          //Don't use new Float32BufferAttribute() because the input array is already typed
          new BufferAttribute(
            new Float32Array(faceGeometry.positions.count * 4),
            4, //red, green, blue and alpha channel (for opacity)
          ),
        );
      if (faceGeometry.normals) {
        facesBufferGeometry.setAttribute('normal', faceGeometry.normals);
      }
      if (faceGeometry.indices) {
        facesBufferGeometry.setIndex(faceGeometry.indices);
      }
      if (faceGeometry.uvs) {
        facesBufferGeometry.setAttribute('uv', faceGeometry.uvs);
      }

      //Note: color attribute is allocated, but populated later (color is either from this.faces metadata or fallsback to this.color)

      this.faceGeometries.push(facesBufferGeometry);
      const facesRenderable = new Mesh(facesBufferGeometry, this.faceMaterial);
      this.facesGroup.add(facesRenderable);
    }
    this.host.fullDetailRep.add(this.facesGroup);
  }

  private loadFaceMetaData(metaData: FaceMetaData) {
    const name = constructEntityName(
      this.id ?? 'undefined',
      ENTITY_TYPES.FACE,
      metaData.index,
    );
    //IMPORTANT: this.faceGeometries must be constucted before StepFaceModel
    const f = new StepFaceModel(name, metaData, this);
    f.setColorAndOpacity([
      ...(f.metaData.color != null
        ? getOrCreateThreeColorArray(f.metaData.color)
        : this.colorRGB),
      MATERIAL_PROPERTIES.OPACITY,
    ]);
    this.faces.set(name, f);
  }

  private loadEdgeGeometries(
    edgeGeometries: Required<GeometryData>['edgeGeometries'],
  ) {
    //Note: No pausing for edgeGeometries creation because there are only a few (with many already packed into the this.part.geometry.edgeGeometries)
    for (const edgeGeometry of edgeGeometries) {
      const edgeChunkCount = edgeGeometry.positions?.count ?? 0;
      const colors = new Float32Array(edgeChunkCount * 4);
      for (let i = 0; i < edgeChunkCount; i++) {
        colors.set(defaultEdgeColorComponents, i * 4);
      }
      const edgesBufferGeometry = new BufferGeometry()
        .setAttribute('position', edgeGeometry.positions)
        //Note: First argument of BufferAttribute must be typed array. (Typescript doesn't specify this but src does)
        //Don't use new Float32BufferAttribute() because the input array is already typed
        .setAttribute('color', new BufferAttribute(colors, 4));

      if (edgeGeometry.indices) {
        edgesBufferGeometry.setIndex(edgeGeometry.indices);
      }

      this.edgeGeometries.push(edgesBufferGeometry);
      const edgesRenderable = new LineSegments(
        edgesBufferGeometry,
        this.edgeMaterial,
      );
      this.edgesGroup.add(edgesRenderable);
    }
    this.host.fullDetailRep.add(this.edgesGroup);
  }

  private loadEdgeMetaData(metaData: EdgeMetaData) {
    const name = constructEntityName(
      this.id ?? 'undefined',
      ENTITY_TYPES.EDGE,
      metaData.index,
    );

    //IMPORTANT: this.edgeGeometries must be constucted before StepEdgeModel
    const e = new StepEdgeModel(name, metaData, this);
    //e.setColorAndOpacity([...
    //  e.metaData.color != null
    //    ? getOrCreateThreeColorArray(e.metaData.color)
    //    : defaultEdgeColorComponents,
    //  1.0
    //]);
    this.edges.set(name, e);
  }

  private async loadText(text: TextData): Promise<StepTextModel | undefined> {
    try {
      const name = constructEntityName(
        this.id ?? 'undefined',
        ENTITY_TYPES.TEXT,
        text.meta.index,
      );
      const textModel = new StepTextModel(name, text, this);
      await textModel.loaded;
      this.texts.set(textModel.name, textModel);
      return textModel;
    } catch (e) {
      console.warn(`Could not load text`, text, e);
      return; //ignore: Can't load this text
    }
  }

  public get textsLoaded() {
    if (this.waitUntilTextsLoaded) {
      //reuse existing promise that is waiting for textsLoaded to resolve
      return this.waitUntilTextsLoaded.waitUntilSet;
    } else if (this.isLoaded) {
      //tested after this.waitUntilTextsLoaded because if loaded synchronously, then isLoaded will
      //be true, but it could still be waiting for the necessary async loading of text
      return Promise.resolve();
    } else if (this.loaded) {
      //still loading async (when async loading finishes, texts are loaded)
      return this.loaded;
    } else {
      //textsLoaded requested before loading started. So initialize waitUntilTextsLoaded
      this.waitUntilTextsLoaded = new WaitUntilSetController();
      return this.waitUntilTextsLoaded.waitUntilSet;
    }
  }

  public get refs() {
    return this.partGeometry.refs;
  }

  public getRef(index: number): RefMetaData | undefined {
    return this.partGeometry.getRef(index);
  }

  public getEntityMeta(index: number, type: MetaDataType) {
    return this.partGeometry.getEntityMeta(index, type);
  }

  private loadRefs() {
    for (const ref of this.part.geometry.refs()) {
      new RefStyleAdapter(ref, this);
    }
  }
}
